import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherArchive,
  FeatherCheck,
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherRotateCcw,
  FeatherUpload,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  AddQuestionDtoQuestionType,
  useDeleteTemplate,
  useGlobalTemplates,
  useImportTemplate,
  usePublishTemplate,
} from "@/api/hooks/useTemplates";
import { GlobalTemplateFormDialog } from "@/components/super-admin/GlobalTemplateFormDialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";

export const Route = createFileRoute("/_authenticated/global-templates")({
  component: GlobalTemplatesPage,
});

interface MappedTemplate {
  id: string;
  name: string;
  description: string | null;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  items: {
    id: string;
    label: string;
    questionType?: AddQuestionDtoQuestionType;
  }[];
  created_at: string;
}

function GlobalTemplatesPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MappedTemplate | null>(
    null,
  );
  const [archivingTemplate, setArchivingTemplate] =
    useState<MappedTemplate | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: templates, isLoading } = useGlobalTemplates();
  const deleteMutation = useDeleteTemplate();
  const publishMutation = usePublishTemplate();
  const importMutation = useImportTemplate();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const json = JSON.parse(text);

      // Validate basic structure
      if (
        !json.name ||
        !Array.isArray(json.questions) ||
        json.questions.length === 0
      ) {
        toast.error("Invalid JSON: must have 'name' and at least one question");
        return;
      }

      importMutation.mutate(json, {
        onSuccess: () => {
          toast.success("Template imported successfully");
        },
        onError: (error) => {
          toast.error("Failed to import template: " + error.message);
        },
      });
    } catch {
      toast.error("Invalid JSON file");
    } finally {
      // Reset input so same file can be selected again
      e.target.value = "";
    }
  };

  // Map API response to component format
  const mappedTemplates: MappedTemplate[] | undefined = templates?.map((t) => ({
    id: t.id,
    name: t.name,
    description: t.description,
    status: t.status,
    items: (t.items || []).map((item, index) => ({
      id: item.id || `${t.id}-item-${index}`,
      label: item.label,
      questionType: item.questionType,
    })),
    created_at: t.createdAt,
  }));

  // Group templates by status
  const publishedTemplates =
    mappedTemplates?.filter((t) => t.status === "PUBLISHED") || [];
  const draftTemplates =
    mappedTemplates?.filter((t) => t.status === "DRAFT") || [];
  const archivedTemplates =
    mappedTemplates?.filter((t) => t.status === "ARCHIVED") || [];

  const handleArchive = () => {
    if (!archivingTemplate) return;

    deleteMutation.mutate(archivingTemplate.id, {
      onSuccess: () => {
        toast.success("Template archived successfully");
        setArchivingTemplate(null);
      },
      onError: (error) => {
        toast.error("Failed to archive template: " + error.message);
      },
    });
  };

  const handlePublish = (id: string) => {
    publishMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Template published successfully");
      },
      onError: (error) => {
        toast.error("Failed to publish template: " + error.message);
      },
    });
  };

  const getStatusBadge = (status: MappedTemplate["status"]) => {
    switch (status) {
      case "PUBLISHED":
        return <Badge variant="success">Published</Badge>;
      case "DRAFT":
        return <Badge variant="warning">Draft</Badge>;
      case "ARCHIVED":
        return <Badge variant="neutral">Archived</Badge>;
    }
  };

  const renderTemplateTable = (
    templateList: MappedTemplate[],
    title: string,
    titleClassName?: string,
  ) => {
    if (templateList.length === 0) return null;

    return (
      <div className="flex w-full flex-col items-start gap-4">
        <span
          className={`text-heading-3 font-heading-3 ${titleClassName || "text-default-font"}`}
        >
          {title}
        </span>
        <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
          <Table
            className={TABLE_MIN_WIDTH.md}
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Template name</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Items</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="items-center justify-end">
                  Actions
                </Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {templateList.map((template) => {
              const isArchived = template.status === "ARCHIVED";

              return (
                <Table.Row
                  key={template.id}
                  className={isArchived ? "opacity-60" : ""}
                >
                  <Table.Cell
                    style={{ verticalAlign: "top" }}
                    className="h-auto py-2"
                  >
                    <span className="text-body-bold font-body-bold text-default-font break-words">
                      {template.name}
                    </span>
                  </Table.Cell>
                  <Table.Cell
                    style={{ verticalAlign: "top" }}
                    className="h-auto py-2"
                  >
                    <span className="text-body font-body text-subtext-color break-words">
                      {template.description || "No description"}
                    </span>
                  </Table.Cell>
                  <Table.Cell
                    style={{ verticalAlign: "top" }}
                    className="h-auto py-2"
                  >
                    <span className="text-body font-body text-default-font">
                      {template.items?.length || 0}{" "}
                      {template.items?.length === 1 ? "item" : "items"}
                    </span>
                  </Table.Cell>
                  <Table.Cell
                    style={{ verticalAlign: "top" }}
                    className="h-auto py-2"
                  >
                    {getStatusBadge(template.status)}
                  </Table.Cell>
                  <Table.Cell
                    variant="aligned-right"
                    style={{ verticalAlign: "top" }}
                    className="h-auto py-2"
                  >
                    <div className="flex items-center justify-end gap-2">
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <IconButton
                            variant="neutral-primary"
                            size="medium"
                            icon={<FeatherMoreHorizontal />}
                          />
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            asChild={true}
                          >
                            <DropdownMenu>
                              {isArchived ? (
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherRotateCcw />}
                                  onClick={() => handlePublish(template.id)}
                                >
                                  Restore & Publish
                                </DropdownMenu.DropdownItem>
                              ) : (
                                <>
                                  {template.status === "DRAFT" && (
                                    <DropdownMenu.DropdownItem
                                      icon={<FeatherCheck />}
                                      onClick={() => handlePublish(template.id)}
                                    >
                                      Publish
                                    </DropdownMenu.DropdownItem>
                                  )}
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherPencil />}
                                    onClick={() => setEditingTemplate(template)}
                                  >
                                    View
                                  </DropdownMenu.DropdownItem>
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherArchive />}
                                    onClick={() =>
                                      setArchivingTemplate(template)
                                    }
                                  >
                                    Archive
                                  </DropdownMenu.DropdownItem>
                                </>
                              )}
                            </DropdownMenu>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json,application/json"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="w-full text-metrics font-metrics text-primary-navy-800">
              Global Templates
            </span>
            <span className="text-body font-body text-subtext-color">
              Create and manage platform-wide templates available to all nursery
              groups
            </span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="neutral-primary"
              icon={<FeatherUpload />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? "Importing..." : "Upload JSON"}
            </Button>
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => setIsCreateOpen(true)}
            >
              Create Template
            </Button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex w-full flex-col items-start gap-8">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : !mappedTemplates?.length ? (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <IconWithLogoBackground variant="neutral" size="x-large" />
            <div className="flex flex-col items-center gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                No global templates yet
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                Global templates are available to all nursery groups on the
                platform. Create your first template to get started.
              </span>
            </div>
            <div className="flex items-center gap-2 pt-4">
              <Button
                variant="neutral-primary"
                icon={<FeatherUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={importMutation.isPending}
              >
                Upload JSON
              </Button>
              <Button
                variant="brand-primary"
                icon={<FeatherPlus />}
                onClick={() => setIsCreateOpen(true)}
              >
                Create Template
              </Button>
            </div>
          </div>
        ) : (
          <>
            {renderTemplateTable(publishedTemplates, "Published")}
            {renderTemplateTable(draftTemplates, "Drafts")}
            {renderTemplateTable(
              archivedTemplates,
              "Archived",
              "text-subtext-color",
            )}
          </>
        )}
      </div>

      {/* Create Dialog */}
      <GlobalTemplateFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        template={null}
      />

      {/* Edit/View Dialog */}
      <GlobalTemplateFormDialog
        open={!!editingTemplate}
        onOpenChange={(open) => !open && setEditingTemplate(null)}
        template={editingTemplate}
      />

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={!!archivingTemplate}
        onOpenChange={() => setArchivingTemplate(null)}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Archive template</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="warning"
                  size="x-large"
                  icon={<FeatherArchive />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Archive template?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to archive{" "}
                    <strong>{archivingTemplate?.name}</strong>? This will make
                    it unavailable to nursery groups.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setArchivingTemplate(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleArchive}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Archiving..." : "Yes, archive"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
